import { useState, useCallback } from 'react';
import './ToolStyles.css';

interface Options {
    length: number;
    uppercase: boolean;
    lowercase: boolean;
    numbers: boolean;
    symbols: boolean;
}

const CHARS = {
    uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    lowercase: 'abcdefghijklmnopqrstuvwxyz',
    numbers: '0123456789',
    symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
};

const PasswordGenerator = () => {
    const [options, setOptions] = useState<Options>({
        length: 16,
        uppercase: true,
        lowercase: true,
        numbers: true,
        symbols: true,
    });
    const [password, setPassword] = useState('');
    const [strength, setStrength] = useState('');

    const generate = useCallback(() => {
        let pool = '';
        for (const [key, val] of Object.entries(CHARS)) {
            if (options[key as keyof typeof CHARS]) pool += val;
        }
        if (!pool) {
            setPassword('');
            setStrength('');
            return;
        }

        const array = new Uint32Array(options.length);
        crypto.getRandomValues(array);
        let result = '';
        for (let i = 0; i < options.length; i++) {
            result += pool[array[i] % pool.length];
        }
        setPassword(result);

        // 简单强度评估
        const n = options.length;
        const types = [options.lowercase, options.uppercase, options.numbers, options.symbols]
            .filter(Boolean).length;
        if (n >= 16 && types >= 3) setStrength('强 🔒');
        else if (n >= 12 && types >= 2) setStrength('中 🟡');
        else setStrength('弱 🔓');
    }, [options]);

    const toggle = (key: keyof Options) => {
        setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <div className="tool-container">
            <div className="input-group">
                <label className="input-label">密码长度: {options.length}</label>
                <input
                    className="tool-input"
                    type="range"
                    min={4}
                    max={64}
                    value={options.length}
                    onChange={(e) => setOptions((p) => ({ ...p, length: Number(e.target.value) }))}
                />
            </div>

            <div className="info-box">
                <h4>字符选项</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {[
                        { key: 'uppercase' as const, label: '大写字母 (A-Z)' },
                        { key: 'lowercase' as const, label: '小写字母 (a-z)' },
                        { key: 'numbers' as const, label: '数字 (0-9)' },
                        { key: 'symbols' as const, label: '特殊符号 (!@#$...)' },
                    ].map((opt) => (
                        <label key={opt.key} style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            fontSize: 13, cursor: 'pointer', color: 'var(--text-secondary)',
                        }}>
                            <input
                                type="checkbox"
                                checked={options[opt.key]}
                                onChange={() => toggle(opt.key)}
                            />
                            {opt.label}
                        </label>
                    ))}
                </div>
            </div>

            <button className="tool-button primary" onClick={generate}>
                生成密码
            </button>

            {password && (
                <div className="output-group">
                    <div className="output-header">
                        <label className="output-label">
                            结果 {strength && `(${strength})`}
                        </label>
                        <button
                            className="copy-button"
                            onClick={() => navigator.clipboard.writeText(password)}
                        >
                            复制
                        </button>
                    </div>
                    <div className="tool-output code">{password}</div>
                </div>
            )}
        </div>
    );
};

export default PasswordGenerator;
